/**
 * FS-C1 공통 패턴 — Bench BS-C1 이식.
 * 출처: picnic-bench apps/bench-web/src/shared/Shell.tsx @62f4f03
 *   (ErrorBanner · Loading · SaveState · Empty · useToasts · ToastHost · ScreenRoot).
 *
 * API 모양과 동작은 Bench 와 같게 두고, Field 에 필요한 것만 보탰다:
 *  - 인라인 스타일 대신 Field 토큰 CSS 클래스(pf-*) — SE/SU 밀도·다크 초록 테마를 따른다.
 *  - data-testid 접두사: Bench `bs-c1-*` → Field `fs-c1-*`(화면 코드 체계 FS-C1).
 *  - 토스트 자동 닫힘: Bench 5초(2026-09-09 결정) → Field **3초**(SCR v0.6 FS-C1-07 "3초 후 사라짐").
 *  - 토스트 닫기 버튼은 밀도 터치 타깃(SE 64px · SU 44px — DR-03)을 따른다.
 *  - ScreenRoot: 셸 틀(AppLayout)이 이미 <main> 을 두므로 <section> 으로 그리고, 제목에 포커스를 옮긴다(SPA 경로 전환 알림).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useT } from "../i18n";
import { useFocusOnMount } from "../hooks/useFocusOnMount";

// ── 오류 배너 ─────────────────────────────────────────────────────────────

/**
 * 오류 배너(교사·운영) — 원인 · 다음 행동 · 문의 코드 3요소를 강제한다(셋 다 필수 인자 — SCR FS-C1 "누락은 디자인 QA 반려").
 * 학생 화면은 코드 없이 다음 행동만 보여야 하므로 {@link FeBanner}(audience="student")를 쓴다.
 */
export function ErrorBanner({
  cause,
  action,
  code,
}: {
  cause: string;
  action: string;
  code: string;
}) {
  const t = useT();
  return (
    <div
      role="alert"
      data-testid="fs-c1-error"
      className="pf-banner pf-banner--err"
      data-fe-code={code}
    >
      <p className="pf-banner__text">
        <b className="pf-banner__cause">{cause}</b>{" "}
        <span className="pf-banner__next">{action}</span>{" "}
        <code className="pf-banner__code">({t("common.codeLabel", { code })})</code>
      </p>
    </div>
  );
}

// ── 로딩 · 저장 상태 · 빈 상태 ───────────────────────────────────────────

/** 로딩 — `bar` 면 얇은 띠(편집기 아래 한 줄 자리)로 그린다. */
export function Loading({ label, bar = false }: { label?: string; bar?: boolean }) {
  const t = useT();
  return (
    <div
      data-testid="fs-c1-loading"
      role="status"
      className={bar ? "pf-bar-line" : "pf-loading-line"}
    >
      {label ?? t("common.loading")}
    </div>
  );
}

/** 저장 상태 — 편집 화면에 상시 노출(토스트로 바꾸지 않는다: 사라지는 것은 상시가 아니다). */
export function SaveState({
  state,
  bar = false,
}: {
  state: "saved" | "saving" | "offline";
  bar?: boolean;
}) {
  const t = useT();
  return (
    <span
      data-testid="fs-c1-savestate"
      data-state={state}
      className={bar ? "pf-bar-line" : "pf-muted"}
    >
      {t(`common.save.${state}`)}
    </span>
  );
}

export function Empty({ label }: { label: string }) {
  return (
    <div data-testid="fs-c1-empty" className="pf-empty">
      {label}
    </div>
  );
}

// ── 토스트 ────────────────────────────────────────────────────────────────

export type ToastKind = "info" | "error";
export type ToastItem = { id: number; kind: ToastKind; text: string; code?: string };

/** Field 기본 3초(SCR v0.6 FS-C1-07). Bench 기본은 5초. */
export const TOAST_MS = 3000;

/** autoDismissMs 뒤 자동으로 닫는다(종류 무관). 같은 말(text+code)은 두 번 쌓지 않는다. */
export function useToasts(autoDismissMs = TOAST_MS) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const timers = useRef<number[]>([]);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);

  const push = useCallback(
    (kind: ToastKind, text: string, code?: string) => {
      setItems((xs) => {
        if (xs.some((x) => x.text === text && x.code === code)) return xs;
        const id = ++seq.current;
        timers.current.push(window.setTimeout(() => dismiss(id), autoDismissMs));
        return [...xs, { id, kind, text, code }];
      });
    },
    [autoDismissMs, dismiss],
  );

  useEffect(
    () => () => {
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    },
    [],
  );

  return { items, push, dismiss };
}

/**
 * 토스트 자리. error 는 role=alert, 나머지는 role=status. 학생 토스트(FS-C1-07)는 피코(🤖)가 말한다 —
 * 코드는 교사·운영 토스트에만 붙인다(code 를 넘기지 않으면 보이지 않음).
 */
export function ToastHost({
  items,
  onDismiss,
  pico = true,
}: {
  items: ToastItem[];
  onDismiss: (id: number) => void;
  /** true 면 앞에 피코 얼굴(학생 토스트 — SCR 와이어 '🤖 …') */
  pico?: boolean;
}) {
  const t = useT();
  if (items.length === 0) return null;
  return (
    <div className="pf-toasts" data-testid="fs-c1-toasts">
      {items.map((item) => (
        <div
          key={item.id}
          className={`pf-toast${item.kind === "error" ? " pf-toast--error" : ""}`}
          data-testid={item.kind === "error" ? "fs-c1-error" : "fs-c1-toast"}
          role={item.kind === "error" ? "alert" : "status"}
          data-read-aloud=""
        >
          <span className="pf-toast__text">
            {pico && <span aria-hidden="true">🤖 </span>}
            {item.text}
            {item.code ? <code className="pf-banner__code"> ({item.code})</code> : null}
          </span>
          <button
            type="button"
            className="pf-toast__close"
            onClick={() => onDismiss(item.id)}
            aria-label={t("common.closeAlert")}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      ))}
    </div>
  );
}

// ── 화면 루트 ─────────────────────────────────────────────────────────────

/**
 * 화면 루트 — data-testid 는 화면 코드 소문자(예: FS-01-02 → "fs-01-02", Bench CSG PR 체크리스트 7 과 같은 규칙).
 * `hideTitle` 이면 제목을 화면에서만 지우고 접근성 트리에는 남긴다. `fill` 은 남은 높이를 채운다.
 */
export function ScreenRoot({
  id,
  title,
  children,
  hideTitle = false,
  fill = false,
  className,
  focusKey,
}: {
  id: string;
  title: string;
  children?: ReactNode;
  hideTitle?: boolean;
  fill?: boolean;
  className?: string;
  /** 바뀌면 제목으로 포커스를 다시 옮긴다(같은 화면 안 단계 전환) */
  focusKey?: unknown;
}) {
  const titleRef = useFocusOnMount<HTMLHeadingElement>(focusKey);
  const hid = `${id.toLowerCase()}-title`;
  return (
    <section
      data-testid={id.toLowerCase()}
      data-screen={id}
      aria-labelledby={hid}
      className={["pf-screen", fill && "pf-screen--fill", className].filter(Boolean).join(" ")}
    >
      <h1 id={hid} ref={titleRef} tabIndex={-1} className={hideTitle ? "pf-sr-only" : "pf-h"}>
        {title}
      </h1>
      {children}
    </section>
  );
}
